import { Fragment, useEffect, useRef, useState, type PointerEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { type DateRange as CalendarRange } from 'react-day-picker'
import { Check, ChevronDown, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'
import {
  DATE_RANGE_GROUPS,
  DATE_RANGE_LABEL_KEYS,
  FLIGHT_RANGE,
  QUICK_DATE_RANGES,
  customRange,
  dateRangeLabel,
  isCustomRange,
  parseCustomRange,
  type DateRange,
  type Flight,
  type PresetDateRange,
} from '@/services/stats/statsRange'

type GroupKey = (typeof DATE_RANGE_GROUPS)[number]['key']

/** How long the pointer rests on a group before its options open. */
const OPEN_DELAY = 100
/**
 * How long a group's options stay after the pointer leaves, so cutting across
 * the next group on the way to them does not swap them out.
 */
const CLOSE_DELAY = 300

interface DateRangeSelectProps {
  value: DateRange
  onChange: (range: DateRange) => void
  /**
   * Dates of the object the stats belong to. Given them, the picker offers the
   * object's whole flight as a period — the one period that is about this
   * campaign rather than about the calendar.
   */
  flight?: Flight
  /**
   * Width utility for the trigger, so callers can match their layout. The
   * default grows with the label rather than cutting a custom range short.
   */
  className?: string
}

/** Turns a stored custom range back into what the calendar selects. */
function toCalendarRange(value: DateRange): CalendarRange | undefined {
  const custom = parseCustomRange(value)
  if (!custom) return undefined
  return { from: new Date(`${custom.from}T00:00:00`), to: new Date(`${custom.to}T00:00:00`) }
}

function RangeOption({
  label,
  selected,
  onClick,
}: {
  label: string
  selected: boolean
  onClick: () => void
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="justify-between gap-6 font-normal"
      onClick={onClick}
    >
      {label}
      {selected && <Check className="size-4" />}
    </Button>
  )
}

interface RangeGroupProps {
  label: string
  sections: PresetDateRange[][]
  value: DateRange
  open: boolean
  onOpenChange: (open: boolean) => void
  onPointerEnter: (event: PointerEvent) => void
  onPointerLeave: (event: PointerEvent) => void
  onPick: (range: DateRange) => void
}

/**
 * One of the "Last" and "This" rows: hovering it opens its periods beside the
 * list, the way a submenu does.
 */
function RangeGroup({
  label,
  sections,
  value,
  open,
  onOpenChange,
  onPointerEnter,
  onPointerLeave,
  onPick,
}: RangeGroupProps) {
  const { t } = useTranslation()
  const rowRef = useRef<HTMLButtonElement>(null)
  // Only a keyboard opening moves focus into the options; a hover leaves it
  // where it is.
  const focusOptions = useRef(false)
  // A period picked from the group names the row, so it reads "Last week"
  // rather than "Last".
  const picked = sections.flat().find((range) => range === value)

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      {/* The options are portalled, but React still counts them as inside this
          row, so moving onto them does not read as leaving it. */}
      <div className="flex flex-col" onPointerEnter={onPointerEnter} onPointerLeave={onPointerLeave}>
        <PopoverAnchor asChild>
          <Button
            ref={rowRef}
            type="button"
            variant="ghost"
            size="sm"
            aria-haspopup="true"
            aria-expanded={open}
            className={cn('justify-between gap-6 font-normal', open && 'bg-accent')}
            onClick={(event) => {
              // A key press (no pointer, so no click count) toggles and moves
              // focus in. A click or tap only opens: hovering has usually
              // opened the group already, and closing it then would surprise.
              const fromKeyboard = event.detail === 0
              focusOptions.current = fromKeyboard
              onOpenChange(fromKeyboard ? !open : true)
            }}
          >
            {picked ? t(DATE_RANGE_LABEL_KEYS[picked]) : label}
            <span className="flex items-center gap-2">
              {picked && <Check className="size-4" />}
              <ChevronRight className="size-4 opacity-50" />
            </span>
          </Button>
        </PopoverAnchor>
        <PopoverContent
          side="left"
          align="start"
          className="w-auto min-w-44 p-1"
          onOpenAutoFocus={(event) => {
            if (!focusOptions.current) event.preventDefault()
            focusOptions.current = false
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault()
            // Closing with focus on an option leaves it on nothing; hand it
            // back to the row.
            if (document.activeElement === document.body) rowRef.current?.focus()
          }}
          onInteractOutside={(event) => {
            // The row itself opens the group, so pressing it must not first
            // dismiss it.
            if (rowRef.current?.contains(event.target as Node)) event.preventDefault()
          }}
        >
          <div className="flex flex-col">
            {sections.map((section, index) => (
              <Fragment key={index}>
                {index > 0 && <Separator className="my-1" />}
                {section.map((range) => (
                  <RangeOption
                    key={range}
                    label={t(DATE_RANGE_LABEL_KEYS[range])}
                    selected={value === range}
                    onClick={() => onPick(range)}
                  />
                ))}
              </Fragment>
            ))}
          </div>
        </PopoverContent>
      </div>
    </Popover>
  )
}

/**
 * Period picker for stats views — the same set of ranges everywhere, plus a
 * custom range picked from a calendar.
 *
 * Today, yesterday and the campaign's own period are one click; the rolling
 * and calendar periods open beside the list from "Last" and "This" on hover.
 *
 * Both views live in one popover rather than a select that opens a second
 * layer: a menu handing off to a popover puts two primitives in a fight over
 * focus, and the calendar loses it. That is also why the groups are popovers
 * of their own rather than menu submenus.
 */
export function DateRangeSelect({
  value,
  onChange,
  flight,
  className = 'w-auto min-w-35',
}: DateRangeSelectProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [showCalendar, setShowCalendar] = useState(false)
  const [openGroup, setOpenGroup] = useState<GroupKey | null>(null)
  const [draft, setDraft] = useState<CalendarRange | undefined>(() => toCalendarRange(value))
  const hoverTimer = useRef<number | undefined>(undefined)

  useEffect(() => () => window.clearTimeout(hoverTimer.current), [])

  // Offered only when the object has dates to run between — `flight` alone does
  // not mean it has both.
  const hasFlight = !!(flight?.startDate && flight?.endDate)

  const openChange = (next: boolean) => {
    setOpen(next)
    // Every opening starts on the list, with the calendar a click away, so the
    // picker always opens the same way whatever period is set.
    if (next) {
      window.clearTimeout(hoverTimer.current)
      setShowCalendar(false)
      setOpenGroup(null)
      setDraft(toCalendarRange(value))
    }
  }

  const pick = (range: DateRange) => {
    onChange(range)
    setOpen(false)
  }

  const apply = () => {
    if (!draft?.from) return
    pick(customRange(draft.from, draft.to ?? draft.from))
  }

  // Hover is for the mouse only: a tap ends with the pointer leaving, which
  // would close the group it just opened.
  const hoverGroup = (key: GroupKey, event: PointerEvent) => {
    if (event.pointerType !== 'mouse') return
    window.clearTimeout(hoverTimer.current)
    if (openGroup === key) return
    hoverTimer.current = window.setTimeout(() => setOpenGroup(key), OPEN_DELAY)
  }

  const leaveGroup = (event: PointerEvent) => {
    if (event.pointerType !== 'mouse') return
    window.clearTimeout(hoverTimer.current)
    hoverTimer.current = window.setTimeout(() => setOpenGroup(null), CLOSE_DELAY)
  }

  const toggleGroup = (key: GroupKey, next: boolean) => {
    window.clearTimeout(hoverTimer.current)
    setOpenGroup((current) => (next ? key : current === key ? null : current))
  }

  return (
    <Popover open={open} onOpenChange={openChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="field"
          size="sm"
          className={cn('justify-between gap-2', className)}
        >
          {dateRangeLabel(value, t)}
          <ChevronDown className="size-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-1" align="end">
        {!showCalendar && (
          <div className="flex min-w-48 flex-col">
            {QUICK_DATE_RANGES.map((range) => (
              <RangeOption
                key={range}
                label={t(DATE_RANGE_LABEL_KEYS[range])}
                selected={value === range}
                onClick={() => pick(range)}
              />
            ))}
            {hasFlight && (
              <RangeOption
                label={t(DATE_RANGE_LABEL_KEYS[FLIGHT_RANGE])}
                selected={value === FLIGHT_RANGE}
                onClick={() => pick(FLIGHT_RANGE)}
              />
            )}
            <Separator className="my-1" />
            {DATE_RANGE_GROUPS.map((group) => (
              <RangeGroup
                key={group.key}
                label={t(group.labelKey)}
                sections={group.sections}
                value={value}
                open={openGroup === group.key}
                onOpenChange={(next) => toggleGroup(group.key, next)}
                onPointerEnter={(event) => hoverGroup(group.key, event)}
                onPointerLeave={leaveGroup}
                onPick={pick}
              />
            ))}
            <Separator className="my-1" />
            <RangeOption
              label={t('stats.customRange')}
              selected={isCustomRange(value)}
              onClick={() => setShowCalendar(true)}
            />
          </div>
        )}
        {showCalendar && (
          <div>
            <Calendar
              mode="range"
              autoFocus
              defaultMonth={draft?.from}
              selected={draft}
              onSelect={setDraft}
              numberOfMonths={2}
              // Stats only exist up to now, so later days are not offerable.
              disabled={{ after: new Date() }}
            />
            <div className="flex items-center justify-end gap-2 border-t p-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowCalendar(false)}
              >
                {t('common.back')}
              </Button>
              <Button type="button" size="sm" disabled={!draft?.from} onClick={apply}>
                {t('common.apply')}
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
